/**
 * Central module registry for dashboards and navigation.
 * Add new modules here to extend DefaultDashboard and DashboardAdmin.
 */
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import BadgeIcon from "@mui/icons-material/Badge";
import FoodBankIcon from "@mui/icons-material/FoodBank";
import LocalDiningIcon from "@mui/icons-material/LocalDining";
import ReceiptIcon from "@mui/icons-material/Receipt";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import SchoolIcon from "@mui/icons-material/School";

import EventsDashboard from "@/containers/events/dashboard";
import ActiveEventDashboard from "@/containers/events/event-dashboard";
import StaffDashboard from "@/containers/staff/dashboard";
import { STAFF_MODULE_PAYROLL_VIEW_PERMISSIONS } from "@/containers/staff/shared/staff-payroll-permissions";
import MadrasaDashboard from "@/containers/madrasa/dashboard/MadrasaDashboard";
import SabilDashboard from "@/containers/sabil/dashboard";
import BookingsRazaDashboard from "@/components/dashboard/BookingsRazaDashboard";
import FmbDashboard from "@/containers/fmb/dashboard";
import MiqaatRsvpDashboard from "@/containers/miqaat-rsvp/dashboard/MiqaatRsvpDashboard";
import AccountsDashboard from "@/containers/accounts/dashboard";
import YearlyNiyaazDashboard from "@/containers/yearly-niyaaz/dashboard";
import YearlyLedgerDashboard from "@/containers/yearly-ledger/YearlyLedgerDashboard";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import LiveTvIcon from "@mui/icons-material/LiveTv";
import HomeWorkIcon from "@mui/icons-material/HomeWork";
import RelayDashboard from "@/containers/relay/dashboard/RelayDashboard";
import ProjectsDashboard from "@/containers/projects/dashboard/ProjectsDashboard";
import ActiveProjectDashboard from "@/containers/projects/dashboard/ActiveProjectDashboard";
import HouseVisitsDashboard from "@/containers/house-visits/dashboard/HouseVisitsDashboard";
import ActiveHouseVisitDashboard from "@/containers/house-visits/dashboard/ActiveHouseVisitDashboard";

import type { ModuleFallback, ModuleRegistryEntry } from "@/types/react-admin-config";

const FALLBACK_NAVIGATE = (path: string): ModuleFallback => ({ type: "navigate", path });
const FALLBACK_DEFAULT: ModuleFallback = { type: "defaultDashboard", path: null };

/**
 * Module registry for DefaultDashboard cards and DashboardAdmin routing.
 * Order determines display order in DefaultDashboard.
 */
export const MODULE_REGISTRY: ModuleRegistryEntry[] = [
  {
    id: "bookings",
    path: "bookings",
    label: "Raza & Bookings",
    description: "Manage raza requests, events and hall bookings",
    icon: EventAvailableIcon,
    permission: "bookings.view",
    permissionsAny: [
      "bookings.view",
      "bookings.dashboard",
      "razaEvents.view",
      "razaEventAttendance.view",
    ],
    dashboardPermission: "bookings.dashboard",
    fallback: FALLBACK_NAVIGATE("/hallBookings"),
    dashboard: BookingsRazaDashboard,
  },
  {
    id: "sabil",
    path: "sabil",
    label: "Sabil",
    description: "View and manage all sabil data",
    icon: BadgeIcon,
    permission: "sabil.view",
    dashboardPermission: "sabil.dashboard",
    fallback: FALLBACK_NAVIGATE("/sabilData"),
    dashboard: SabilDashboard,
  },
  {
    id: "fmb",
    path: "fmb",
    label: "FMB",
    description: "View and manage all fmb data",
    icon: LocalDiningIcon,
    permission: "fmbData.view",
    dashboardPermission: "fmbData.view",
    fallback: FALLBACK_NAVIGATE("/fmbData"),
    dashboard: FmbDashboard,
  },

  {
    id: "events",
    path: "events",
    label: "Niyaaz Events",
    description: "Manage Shehrullah niyaaz events and collection workflow",
    icon: FoodBankIcon,
    permission: "event.view",
    dashboardPermission: null,
    fallback: FALLBACK_DEFAULT,
    hasChildDashboard: true,
    dashboard: EventsDashboard,
    childDashboard: ActiveEventDashboard,
  },
  {
    id: "staff",
    path: "staff",
    label: "Staff",
    description: "View and manage all staff",
    icon: BadgeIcon,
    permission: "employees.view",
    permissionsAny: [
      "employees.view",
      "myAttendance.view",
      "staff.dashboard",
      ...STAFF_MODULE_PAYROLL_VIEW_PERMISSIONS,
    ],
    dashboardPermission: "staff.dashboard",
    fallback: FALLBACK_NAVIGATE("/myAttendance"),
    dashboard: StaffDashboard,
  },
  {
    id: "madrasa",
    path: "madrasa",
    label: "Madrasa",
    description: "Students, fees and madrasa back office",
    icon: SchoolIcon,
    permission: "madrasaStudents.view",
    permissionsAny: [
      "madrasaStudents.view",
      "madrasaEnrollments.view",
      "madrasaClasses.view",
      "madrasaSettings.view",
      "madrasaFeeSetup.view",
      "madrasaFees.view",
    ],
    dashboardPermission: null,
    fallback: FALLBACK_NAVIGATE("/madrasaStudents"),
    dashboard: MadrasaDashboard,
  },
  {
    id: "miqaatRsvp",
    path: "miqaatRsvp",
    label: "Miqaat RSVP",
    description: "Miqaat RSVPs and attendance",
    icon: ReceiptIcon,
    permission: "miqaats.view",
    dashboardPermission: "miqaats.view",
    fallback: FALLBACK_NAVIGATE("/miqaats"),
    dashboard: MiqaatRsvpDashboard,
  },
  {
    id: "accounts",
    path: "accounts",
    label: "Accounts",
    description: "View and manage all accounts",
    icon: AccountBalanceIcon,
    permission: "accounts.view",
    dashboardPermission: "accounts.view",
    fallback: FALLBACK_DEFAULT,
    dashboard: AccountsDashboard,
  },
  {
    id: "yearlyNiyaaz",
    path: "yearlyNiyaaz",
    label: "Yearly Niyaaz",
    description: "View and manage yearly niyaaz and receipts",
    icon: VolunteerActivismIcon,
    permission: "yearlyNiyaaz.view",
    dashboardPermission: null,
    fallback: FALLBACK_NAVIGATE("/yearlyNiyaaz"),
    dashboard: YearlyNiyaazDashboard,
  },
  {
    id: "yearlyLedger",
    path: "yearlyLedger",
    label: "Yearly Ledgers",
    description: "View and manage yearly ledgers.",
    icon: AccountBalanceIcon,
    permission: "yearlyLedger.view",
    dashboardPermission: "yearlyLedger.dashboard",
    fallback: FALLBACK_NAVIGATE("/yearly-ledger"),
    dashboard: YearlyLedgerDashboard,
  },
  {
    id: "projects",
    path: "projects",
    label: "Projects",
    description: "Project budgets, takhmeen, Inayat, and Karzan",
    icon: AccountBalanceWalletIcon,
    permission: "projects.view",
    permissionsAny: ["projects.view", "projects.dashboard"],
    dashboardPermission: "projects.dashboard",
    fallback: FALLBACK_NAVIGATE("/projects"),
    hasChildDashboard: true,
    dashboard: ProjectsDashboard,
    childDashboard: ActiveProjectDashboard,
  },
  {
    id: "relay",
    path: "relay",
    label: "Relay",
    description: "Manage relay stream events and access grants",
    icon: LiveTvIcon,
    permission: "relayAccessRequests.view",
    permissionsAny: ["relayAccessRequests.view", "relayEvents.view"],
    dashboardPermission: null,
    fallback: FALLBACK_NAVIGATE("/relay-events"),
    dashboard: RelayDashboard,
  },
  {
    id: "houseVisits",
    path: "houseVisits",
    label: "House Visits",
    description: "Plan weekday house-visit slots and run daily routes",
    icon: HomeWorkIcon,
    permission: "houseVisitEvents.view",
    permissionsAny: ["houseVisitEvents.view", "houseVisitSlots.view", "houseVisitStops.view"],
    dashboardPermission: null,
    fallback: FALLBACK_DEFAULT,
    hasChildDashboard: true,
    dashboard: HouseVisitsDashboard,
    childDashboard: ActiveHouseVisitDashboard,
  },
];

export const getModuleByPath = (path: string): ModuleRegistryEntry | undefined =>
  MODULE_REGISTRY.find((m) => m.path === path);
